import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {ActivityIndicator, Platform, Pressable, View} from 'react-native'
import {msg} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {type Slider as SliderType} from '@miblanchard/react-native-slider'
import type * as HlsTypes from 'hls.js'

import {tellusTvDebug} from '#/lib/tv/debug'
import {type TVSegment} from '#/lib/tv/tvTypes'
import {atoms as a, useTheme} from '#/alf'
import {Pause_Filled_Corner0_Rounded as PauseIcon} from '#/components/icons/Pause'
import {Play_Filled_Corner0_Rounded as PlayIcon} from '#/components/icons/Play'
import {formatTime} from '#/components/Post/Embed/VideoEmbed/VideoEmbedInner/web-controls/utils'
import {SeamlessPlayer} from '#/components/tv/SeamlessPlayer'
import {Text} from '#/components/Typography'

export type PlayableSegment = {
  uri: string
  order: number
  durationMs: number
  src: string
  thumb?: string
}

type Props = {
  segments: PlayableSegment[]
  totalDurationMs: number
  initialTimeMs?: number
  onProgress?: (info: {
    timeMs: number
    segmentIndex: number
    offsetMs: number
  }) => void
  onError?: (error: Error) => void
}

type SegmentMapping = {index: number; offsetMs: number}
const BUFFER_THRESHOLD_SECONDS = 0.8
const EARLY_SWAP_SECONDS = 0.25
const CROSSFADE_DURATION_MS = 80
const HLS_EXTENSION_RE = /\.m3u8($|\?|#)/i

function logDebug(event: string, payload?: unknown) {
  tellusTvDebug(event, payload)
}

function isHlsSource(src: string) {
  return HLS_EXTENSION_RE.test(src)
}

function shouldUseCors(src: string) {
  if (typeof window === 'undefined') return false
  try {
    const url = new URL(src, window.location.href)
    return url.origin !== window.location.origin
  } catch {
    return false
  }
}

function applyCrossOrigin(
  video: HTMLVideoElement,
  src: string,
  shouldApply: boolean,
) {
  if (!shouldApply) {
    video.removeAttribute('crossorigin')
    return
  }
  if (shouldUseCors(src)) {
    video.crossOrigin = 'anonymous'
    video.setAttribute('crossorigin', 'anonymous')
  } else {
    video.removeAttribute('crossorigin')
  }
}

function canUseNativeHls(video?: HTMLVideoElement | null) {
  const canPlay =
    video?.canPlayType?.('application/vnd.apple.mpegurl') ||
    video?.canPlayType?.('application/x-mpegURL')
  if (canPlay === 'probably' || canPlay === 'maybe') return true
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  const isSafari =
    /^((?!chrome|android).)*safari/i.test(ua) &&
    !/CriOS/i.test(ua) &&
    !/FxiOS/i.test(ua)
  const isIOSWebKit = /iP(ad|hone|od)/i.test(ua) && !/CriOS|FxiOS/i.test(ua)
  return isSafari || isIOSWebKit
}

function buildOffsets(segments: PlayableSegment[]) {
  const offsets: number[] = []
  let total = 0
  for (const segment of segments) {
    offsets.push(total)
    total += segment.durationMs || 0
  }
  return {offsets, total}
}

function mapTimeToSegment(timeMs: number, offsets: number[]): SegmentMapping {
  for (let i = offsets.length - 1; i >= 0; i--) {
    if (timeMs >= offsets[i]) {
      return {index: i, offsetMs: timeMs - offsets[i]}
    }
  }
  return {index: 0, offsetMs: timeMs}
}

export function TellusTVSegmentPlayer({
  segments,
  totalDurationMs,
  initialTimeMs,
  onProgress,
  onError,
}: Props) {
  if (Platform.OS !== 'web') {
    const fallback: TVSegment[] = segments.map(seg => ({
      uri: seg.uri,
      durationMs: seg.durationMs,
      order: seg.order,
    }))
    return (
      <SeamlessPlayer
        segments={fallback}
        totalDurationMs={totalDurationMs}
        initialTimeMs={initialTimeMs}
        onProgress={onProgress}
        onError={onError}
      />
    )
  }

  return (
    <TellusTVSegmentPlayerWeb
      segments={segments}
      totalDurationMs={totalDurationMs}
      initialTimeMs={initialTimeMs}
      onProgress={onProgress}
      onError={onError}
    />
  )
}

function TellusTVSegmentPlayerWeb({
  segments,
  totalDurationMs,
  initialTimeMs,
  onProgress,
  onError,
}: Props) {
  const {_} = useLingui()
  const t = useTheme()
  const videoARef = useRef<HTMLVideoElement | null>(null)
  const videoBRef = useRef<HTMLVideoElement | null>(null)
  const retryRef = useRef<Record<number, number>>({})
  const [activeIndex, setActiveIndex] = useState(0)
  const [activeSlot, setActiveSlot] = useState<0 | 1>(0)
  const [currentTimeMs, setCurrentTimeMs] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isBuffering, setIsBuffering] = useState(false)
  const lastProgressRef = useRef(0)
  const pendingSeekRef = useRef<number | null>(null)
  const switchingRef = useRef(false)
  const shouldPlayRef = useRef(false)
  const suppressPauseRef = useRef(false)
  const earlySwapRef = useRef(false)
  const hlsARef = useRef<HlsTypes.default | null>(null)
  const hlsBRef = useRef<HlsTypes.default | null>(null)
  const slotSrcRef = useRef<[string | null, string | null]>([null, null])
  const slotReadyRef = useRef<[boolean, boolean]>([false, false])
  const pendingAdvanceRef = useRef(false)
  const crossfadeRef = useRef<number | null>(null)
  const loadingLabel = _(msg`Loading…`)
  const errorLabel = _(msg`Unable to play this video.`)

  const {offsets} = useMemo(() => buildOffsets(segments), [segments])
  const activeSegment = segments[activeIndex]
  const activeVideoRef = activeSlot === 0 ? videoARef : videoBRef
  const inactiveVideoRef = activeSlot === 0 ? videoBRef : videoARef
  const activeHlsRef = activeSlot === 0 ? hlsARef : hlsBRef
  const inactiveHlsRef = activeSlot === 0 ? hlsBRef : hlsARef

  useEffect(() => {
    logDebug('player mount', {
      segments: segments.length,
      totalDurationMs,
    })
    return () => {
      logDebug('player unmount', {
        segments: segments.length,
      })
    }
  }, [segments.length, totalDurationMs])

  const clearCrossfade = useCallback(() => {
    if (crossfadeRef.current != null) {
      window.cancelAnimationFrame(crossfadeRef.current)
      crossfadeRef.current = null
    }
  }, [])

  const switchTo = useCallback(
    (
      index: number,
      offsetMs?: number,
      options?: {buffering?: boolean; preserveCrossfade?: boolean},
    ) => {
      if (!segments[index]) return
      if (!options?.preserveCrossfade) {
        clearCrossfade()
      }
      setIsBuffering(options?.buffering ?? true)
      switchingRef.current = true
      pendingSeekRef.current = offsetMs ?? 0
      earlySwapRef.current = false
      setActiveIndex(index)
    },
    [clearCrossfade, segments],
  )

  const handleEnded = useCallback(() => {
    const nextIndex = activeIndex + 1
    if (nextIndex >= segments.length) {
      return
    }
    const nextSlot = activeSlot === 0 ? 1 : 0
    const ready = slotReadyRef.current[nextSlot]
    pendingAdvanceRef.current = false
    suppressPauseRef.current = true
    shouldPlayRef.current = true
    setIsPlaying(true)
    const nextVideo = inactiveVideoRef.current
    if (nextVideo) {
      nextVideo.muted = false
      nextVideo.volume = 1
      nextVideo.play().catch(() => {})
    }
    setActiveSlot(prev => (prev === 0 ? 1 : 0))
    switchTo(nextIndex, 0, {buffering: !ready})
  }, [activeIndex, activeSlot, inactiveVideoRef, segments.length, switchTo])

  const startCrossfade = useCallback(
    (current: HTMLVideoElement, next: HTMLVideoElement) => {
      clearCrossfade()
      const start = window.performance?.now?.() ?? Date.now()
      const step = (now: number) => {
        const elapsed = now - start
        const ratio = Math.min(1, elapsed / CROSSFADE_DURATION_MS)
        next.volume = ratio
        current.volume = 1 - ratio
        if (ratio < 1) {
          crossfadeRef.current = window.requestAnimationFrame(step)
        } else {
          current.pause()
          current.currentTime = 0
          current.muted = true
          current.volume = 0
          next.muted = false
          next.volume = 1
          crossfadeRef.current = null
        }
      }
      next.volume = 0
      current.volume = 1
      crossfadeRef.current = window.requestAnimationFrame(step)
    },
    [clearCrossfade],
  )

  const handleError = useCallback(
    (err?: unknown) => {
      if (activeHlsRef.current) {
        activeHlsRef.current.destroy()
        activeHlsRef.current = null
      }
      logDebug('player error', {
        index: activeIndex,
        src: activeSegment?.src,
        error:
          err instanceof Error
            ? err.message
            : typeof err === 'string'
              ? err
              : 'unknown',
      })
      const attempt = retryRef.current[activeIndex] || 0
      if (attempt < 1) {
        retryRef.current[activeIndex] = attempt + 1
        const video = activeVideoRef.current
        if (video) {
          video.load()
          if (isPlaying) {
            video.play().catch(() => {})
          }
        }
        return
      }
      setIsBuffering(false)
      setErrorMessage(errorLabel)
      onError?.(err instanceof Error ? err : new Error('Segment failed'))
      const nextIndex = activeIndex + 1
      if (nextIndex < segments.length) {
        switchTo(nextIndex, 0, {buffering: true})
      }
    },
    [
      activeIndex,
      activeSegment?.src,
      activeHlsRef,
      activeVideoRef,
      isPlaying,
      onError,
      segments.length,
      switchTo,
      errorLabel,
    ],
  )

  useEffect(() => {
    if (!activeSegment?.src) return
    const video = activeVideoRef.current
    if (!video) return
    const slotIndex = activeSlot
    const src = activeSegment.src
    if (slotSrcRef.current[slotIndex] === src) {
      if (pendingSeekRef.current != null) {
        video.currentTime = pendingSeekRef.current / 1000
        pendingSeekRef.current = null
      }
      if (switchingRef.current && shouldPlayRef.current) {
        video.play().catch(() => {})
      }
      switchingRef.current = false
      setIsBuffering(false)
      return
    }
    setErrorMessage(null)
    setIsBuffering(true)
    if (activeHlsRef.current) {
      activeHlsRef.current.destroy()
      activeHlsRef.current = null
    }
    slotSrcRef.current[slotIndex] = src
    const isHls = isHlsSource(src)
    const useNativeHls = isHls && canUseNativeHls(video)
    applyCrossOrigin(video, src, isHls && !useNativeHls)
    logDebug('load segment', {
      index: activeIndex,
      src,
      kind: isHls ? 'hls' : 'mp4',
    })
    if (isHls) {
      if (useNativeHls) {
        logDebug('playlist fetch start', {
          index: activeIndex,
          src,
          mode: 'native-hls',
        })
        video.src = src
      } else {
        import('hls.js')
          .then(mod => {
            const Hls = (mod.default || mod) as typeof HlsTypes.default
            if (Hls.isSupported()) {
              const hls = new Hls({
                maxBufferLength: 20,
                backBufferLength: 30,
                xhrSetup: xhr => {
                  xhr.withCredentials = false
                },
              })
              activeHlsRef.current = hls
              hls.attachMedia(video)
              hls.on(Hls.Events.MEDIA_ATTACHED, () => {
                logDebug('playlist fetch start', {
                  index: activeIndex,
                  src,
                  mode: 'hls.js',
                })
                hls.loadSource(src)
              })
              hls.on(Hls.Events.MANIFEST_PARSED, (_event, data) => {
                logDebug('playlist fetch end', {
                  index: activeIndex,
                  src,
                  levels: data?.levels?.length ?? 0,
                })
              })
              hls.on(Hls.Events.ERROR, (_event, data) => {
                logDebug('hls error', {
                  fatal: data?.fatal,
                  type: data?.type,
                  details: data?.details,
                })
                if (data?.fatal) {
                  handleError(data?.error || new Error('HLS fatal error'))
                }
              })
            } else {
              video.src = src
            }
          })
          .catch(() => {
            video.src = src
          })
      }
    } else {
      video.src = src
    }
    if (activeSegment.thumb && !video.poster) {
      video.poster = activeSegment.thumb
    }
    video.muted = false
    video.volume = 1
    video.load()
    if (isPlaying) {
      video.play().catch(() => {})
    }
  }, [
    activeIndex,
    activeHlsRef,
    activeSegment,
    activeSlot,
    activeVideoRef,
    handleError,
    isPlaying,
  ])

  useEffect(() => {
    const video = activeVideoRef.current
    if (!video) return

    const onTimeUpdate = () => {
      const offsetMs = video.currentTime * 1000
      const timeMs = offsets[activeIndex] + offsetMs
      setCurrentTimeMs(timeMs)
      if (Number.isFinite(video.duration) && video.duration > 0) {
        const remaining = video.duration - video.currentTime
        if (remaining <= BUFFER_THRESHOLD_SECONDS) {
          pendingAdvanceRef.current = true
        }
        if (
          !earlySwapRef.current &&
          remaining <= EARLY_SWAP_SECONDS &&
          activeIndex + 1 < segments.length
        ) {
          const nextSlot = activeSlot === 0 ? 1 : 0
          if (slotReadyRef.current[nextSlot]) {
            const nextVideo = inactiveVideoRef.current
            if (nextVideo) {
              earlySwapRef.current = true
              suppressPauseRef.current = true
              shouldPlayRef.current = true
              setIsPlaying(true)
              nextVideo.muted = false
              nextVideo.currentTime = 0
              nextVideo.play().catch(() => {})
              startCrossfade(video, nextVideo)
              setActiveSlot(prev => (prev === 0 ? 1 : 0))
              switchTo(activeIndex + 1, 0, {
                buffering: false,
                preserveCrossfade: true,
              })
              pendingAdvanceRef.current = false
            }
          }
        }
      }
      if (onProgress) {
        const now = Date.now()
        if (now - lastProgressRef.current > 1000) {
          lastProgressRef.current = now
          onProgress({
            timeMs,
            segmentIndex: activeIndex,
            offsetMs,
          })
        }
      }
    }

    const onLoadedMetadata = () => {
      logDebug('segment ready', {
        index: activeIndex,
        src: slotSrcRef.current[activeSlot],
      })
      setIsBuffering(false)
      if (pendingSeekRef.current != null) {
        video.currentTime = pendingSeekRef.current / 1000
        pendingSeekRef.current = null
      }
      if (switchingRef.current && shouldPlayRef.current) {
        video.play().catch(() => {})
      }
      switchingRef.current = false
      if (crossfadeRef.current == null) {
        clearCrossfade()
      }
    }

    const onPlay = () => {
      setIsPlaying(true)
      shouldPlayRef.current = true
      setIsBuffering(false)
    }
    const onPlaying = () => {
      setIsBuffering(false)
    }
    const onWaiting = () => {
      setIsBuffering(true)
    }
    const onStalled = () => {
      setIsBuffering(true)
    }
    const onPause = () => {
      if (suppressPauseRef.current) {
        suppressPauseRef.current = false
        return
      }
      setIsPlaying(false)
      shouldPlayRef.current = false
    }

    video.addEventListener('timeupdate', onTimeUpdate)
    video.addEventListener('loadedmetadata', onLoadedMetadata)
    video.addEventListener('canplay', onLoadedMetadata)
    video.addEventListener('ended', handleEnded)
    video.addEventListener('error', handleError)
    video.addEventListener('play', onPlay)
    video.addEventListener('playing', onPlaying)
    video.addEventListener('waiting', onWaiting)
    video.addEventListener('stalled', onStalled)
    video.addEventListener('pause', onPause)

    return () => {
      video.removeEventListener('timeupdate', onTimeUpdate)
      video.removeEventListener('loadedmetadata', onLoadedMetadata)
      video.removeEventListener('canplay', onLoadedMetadata)
      video.removeEventListener('ended', handleEnded)
      video.removeEventListener('error', handleError)
      video.removeEventListener('play', onPlay)
      video.removeEventListener('playing', onPlaying)
      video.removeEventListener('waiting', onWaiting)
      video.removeEventListener('stalled', onStalled)
      video.removeEventListener('pause', onPause)
    }
  }, [
    activeIndex,
    activeSlot,
    activeVideoRef,
    inactiveVideoRef,
    clearCrossfade,
    handleEnded,
    handleError,
    offsets,
    onProgress,
    segments.length,
    startCrossfade,
    switchTo,
  ])

  useEffect(() => {
    if (!initialTimeMs || !segments.length) return
    const {index, offsetMs} = mapTimeToSegment(initialTimeMs, offsets)
    pendingSeekRef.current = offsetMs
    setActiveIndex(index)
    setCurrentTimeMs(initialTimeMs)
    setIsBuffering(false)
  }, [initialTimeMs, offsets, segments.length])

  useEffect(() => {
    const next = segments[activeIndex + 1]
    if (!next?.src) return
    const video = inactiveVideoRef.current
    if (!video) return
    const slotIndex = activeSlot === 0 ? 1 : 0
    const src = next.src
    if (slotSrcRef.current[slotIndex] === src) return
    slotReadyRef.current[slotIndex] = false
    slotSrcRef.current[slotIndex] = src
    if (inactiveHlsRef.current) {
      inactiveHlsRef.current.destroy()
      inactiveHlsRef.current = null
    }
    const onCanPlay = () => {
      slotReadyRef.current[slotIndex] = true
      if (pendingAdvanceRef.current) {
        suppressPauseRef.current = true
        shouldPlayRef.current = true
        setIsPlaying(true)
        setActiveSlot(prev => (prev === 0 ? 1 : 0))
        switchTo(activeIndex + 1, 0, {buffering: false})
        pendingAdvanceRef.current = false
      }
    }
    video.addEventListener('loadeddata', onCanPlay, {once: true})
    video.addEventListener('canplay', onCanPlay, {once: true})
    const isHls = isHlsSource(src)
    const useNativeHls = isHls && canUseNativeHls(video)
    applyCrossOrigin(video, src, isHls && !useNativeHls)
    logDebug('preload segment', {
      index: activeIndex + 1,
      src,
      kind: isHls ? 'hls' : 'mp4',
    })
    if (isHls) {
      if (useNativeHls) {
        logDebug('preload playlist start', {
          index: activeIndex + 1,
          src,
          mode: 'native-hls',
        })
        video.src = src
      } else {
        import('hls.js')
          .then(mod => {
            const Hls = (mod.default || mod) as typeof HlsTypes.default
            if (Hls.isSupported()) {
              const hls = new Hls({
                maxBufferLength: 20,
                backBufferLength: 30,
                xhrSetup: xhr => {
                  xhr.withCredentials = false
                },
              })
              inactiveHlsRef.current = hls
              hls.attachMedia(video)
              hls.on(Hls.Events.MEDIA_ATTACHED, () => {
                logDebug('preload playlist start', {
                  index: activeIndex + 1,
                  src,
                  mode: 'hls.js',
                })
                hls.loadSource(src)
              })
              hls.on(Hls.Events.MANIFEST_PARSED, (_event, data) => {
                logDebug('preload playlist end', {
                  index: activeIndex + 1,
                  src,
                  levels: data?.levels?.length ?? 0,
                })
              })
              hls.on(Hls.Events.ERROR, (_event, data) => {
                logDebug('preload hls error', {
                  fatal: data?.fatal,
                  type: data?.type,
                  details: data?.details,
                })
              })
            } else {
              video.src = src
            }
          })
          .catch(() => {
            video.src = src
          })
      }
    } else {
      video.src = src
    }
    video.muted = true
    video.volume = 0
    video.load()
    return () => {
      video.removeEventListener('loadeddata', onCanPlay)
      video.removeEventListener('canplay', onCanPlay)
    }
  }, [
    activeIndex,
    activeSlot,
    inactiveHlsRef,
    inactiveVideoRef,
    segments,
    switchTo,
  ])

  const onSeek = useCallback(
    (value: number | number[]) => {
      const nextValue = Array.isArray(value) ? value[0] : value
      const seekMs = Math.max(0, Math.min(totalDurationMs, nextValue))
      const {index, offsetMs} = mapTimeToSegment(seekMs, offsets)
      if (index !== activeIndex) {
        switchTo(index, offsetMs)
      } else {
        const video = activeVideoRef.current
        if (video) {
          video.currentTime = offsetMs / 1000
        }
      }
      setCurrentTimeMs(seekMs)
    },
    [activeIndex, activeVideoRef, offsets, totalDurationMs, switchTo],
  )

  const togglePlay = useCallback(() => {
    const video = activeVideoRef.current
    if (!video) return
    if (video.paused) {
      shouldPlayRef.current = true
      setIsPlaying(true)
      video.play().catch(() => {})
    } else {
      shouldPlayRef.current = false
      setIsPlaying(false)
      video.pause()
    }
  }, [activeVideoRef])

  const seekBySeconds = useCallback(
    (deltaSeconds: number) => {
      const video = activeVideoRef.current
      const nextMs = Math.max(
        0,
        Math.min(totalDurationMs, currentTimeMs + deltaSeconds * 1000),
      )
      const {index, offsetMs} = mapTimeToSegment(nextMs, offsets)
      if (index !== activeIndex) {
        switchTo(index, offsetMs)
      } else if (video) {
        video.currentTime = offsetMs / 1000
      }
      setCurrentTimeMs(nextMs)
    },
    [
      activeIndex,
      activeVideoRef,
      currentTimeMs,
      offsets,
      switchTo,
      totalDurationMs,
    ],
  )

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const tag = (event.target as HTMLElement | null)?.tagName?.toLowerCase()
      if (tag === 'input' || tag === 'textarea') return
      if (event.key === 'j') {
        event.preventDefault()
        seekBySeconds(-10)
      } else if (event.key === 'l') {
        event.preventDefault()
        seekBySeconds(10)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [seekBySeconds])

  useEffect(() => {
    if (crossfadeRef.current != null) return
    const active = activeVideoRef.current
    const inactive = inactiveVideoRef.current
    if (active) {
      active.muted = false
      active.volume = 1
    }
    if (inactive) {
      inactive.muted = true
      inactive.volume = 0
    }
  }, [activeSlot, activeVideoRef, inactiveVideoRef])

  const Slider = require('@miblanchard/react-native-slider')
    .Slider as typeof SliderType

  const statusLabel = errorMessage ?? (isBuffering ? loadingLabel : null)
  const showSpinner = Boolean(isBuffering && !errorMessage)

  return (
    <View style={[a.gap_sm]}>
      <View
        style={[
          a.rounded_md,
          a.overflow_hidden,
          a.relative,
          {backgroundColor: '#000', aspectRatio: 16 / 9, width: '100%'},
        ]}>
        <video
          ref={videoARef}
          controls={false}
          preload="auto"
          playsInline
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            inset: 0,
            opacity: activeSlot === 0 ? 1 : 0,
            objectFit: 'cover',
            transition: 'opacity 80ms linear',
          }}
        />
        {statusLabel ? (
          <View
            pointerEvents="none"
            style={[
              a.absolute,
              a.inset_0,
              a.align_center,
              a.justify_center,
              {backgroundColor: 'rgba(0,0,0,0.35)', zIndex: 3},
            ]}>
            {showSpinner ? <ActivityIndicator color={t.palette.white} /> : null}
            <Text style={[a.text_sm, a.mt_sm, {color: t.palette.white}]}>
              {statusLabel}
            </Text>
          </View>
        ) : null}
        <video
          ref={videoBRef}
          controls={false}
          preload="auto"
          playsInline
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            inset: 0,
            opacity: activeSlot === 1 ? 1 : 0,
            objectFit: 'cover',
            transition: 'opacity 80ms linear',
          }}
        />
        <Pressable
          accessibilityRole="button"
          onPress={togglePlay}
          style={[a.absolute, a.inset_0, a.align_center, a.justify_center]}>
          {!isPlaying ? (
            <View
              style={[
                a.rounded_full,
                a.align_center,
                a.justify_center,
                {width: 72, height: 72, backgroundColor: 'rgba(0,0,0,0.55)'},
              ]}>
              <PlayIcon width={28} height={28} fill="white" />
            </View>
          ) : null}
        </Pressable>
      </View>
      <View>
        <Slider
          value={currentTimeMs}
          minimumValue={0}
          maximumValue={totalDurationMs || 1}
          onSlidingComplete={onSeek}
          trackStyle={{height: 3, borderRadius: 999}}
          thumbStyle={{
            width: 12,
            height: 12,
            borderRadius: 999,
            backgroundColor: t.palette.primary_500,
          }}
          minimumTrackTintColor={t.palette.primary_500}
          maximumTrackTintColor={t.atoms.bg_contrast_25.backgroundColor}
        />
        <View style={[a.flex_row, a.align_center, a.justify_between, a.pt_xs]}>
          <View style={[a.flex_row, a.align_center, a.gap_sm]}>
            <Pressable
              accessibilityRole="button"
              onPress={togglePlay}
              style={[a.rounded_full, a.p_xs]}>
              {isPlaying ? (
                <PauseIcon
                  width={18}
                  height={18}
                  fill={t.palette.contrast_700}
                />
              ) : (
                <PlayIcon
                  width={18}
                  height={18}
                  fill={t.palette.contrast_700}
                />
              )}
            </Pressable>
            <Pressable
              onPress={() => seekBySeconds(-10)}
              accessibilityLabel={_(msg`Back 10 seconds`)}
              accessibilityHint={_(msg`Skip back 10 seconds`)}
              style={[a.rounded_full, a.p_xs]}>
              <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
                -10s
              </Text>
            </Pressable>
            <Pressable
              onPress={() => seekBySeconds(10)}
              accessibilityLabel={_(msg`Forward 10 seconds`)}
              accessibilityHint={_(msg`Skip forward 10 seconds`)}
              style={[a.rounded_full, a.p_xs]}>
              <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
                +10s
              </Text>
            </Pressable>
          </View>
          <View style={[a.flex_row, a.align_center, a.gap_xs]}>
            <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
              {formatTime(currentTimeMs / 1000)}
            </Text>
            <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>/</Text>
            <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
              {formatTime(totalDurationMs / 1000)}
            </Text>
          </View>
        </View>
      </View>
    </View>
  )
}
